import { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link } from 'react-router-dom'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline'
import { manualChapters, getChapterById, getSectionById } from '@/data/manual'
import { useTheme } from '@/contexts/ThemeContext'

interface HelpContentProps {
  chapterId: string
  sectionId: string
}

export default function HelpContent({ chapterId, sectionId }: HelpContentProps) {
  const { theme } = useTheme()
  const appName = theme.appName || 'GrowUp!'

  const chapter = getChapterById(chapterId)
  const section = getSectionById(chapterId, sectionId)

  // Replace {{APP_NAME}} placeholder with actual app name
  const processedContent = useMemo(() => {
    if (!section?.content) return ''
    return section.content.replace(/\{\{APP_NAME\}\}/g, appName)
  }, [section?.content, appName])

  // Calculate prev/next navigation
  const navigation = useMemo(() => {
    const allSections: Array<{ chapterId: string; sectionId: string; title: string; chapterTitle: string }> = []

    for (const ch of manualChapters) {
      for (const sec of ch.sections) {
        allSections.push({
          chapterId: ch.id,
          sectionId: sec.id,
          title: sec.title,
          chapterTitle: ch.title
        })
      }
    }

    const currentIndex = allSections.findIndex(
      s => s.chapterId === chapterId && s.sectionId === sectionId
    )

    return {
      prev: currentIndex > 0 ? allSections[currentIndex - 1] : null,
      next: currentIndex < allSections.length - 1 ? allSections[currentIndex + 1] : null
    }
  }, [chapterId, sectionId])

  if (!chapter || !section) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <BookOpenIcon className="h-12 w-12 mb-4" />
        <p className="text-lg font-medium">Sezione non trovata</p>
        <p className="text-sm">Seleziona una sezione dall'indice</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/help" className="hover:text-primary-600">Manuale</Link>
        <span>/</span>
        <Link to={`/help/${chapter.id}/${chapter.sections[0]?.id}`} className="hover:text-primary-600">
          {chapter.title}
        </Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">{section.title}</span>
      </nav>

      {/* Chapter/Section Header */}
      <header className="mb-8">
        <p className="text-sm font-medium text-primary-600 mb-1">
          Capitolo {chapter.number}
        </p>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {section.title}
        </h1>
      </header>

      {/* Content */}
      <article className="prose prose-slate max-w-none prose-headings:scroll-mt-20 prose-img:rounded-lg prose-img:shadow-md">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Custom image renderer to load from assets
            img: ({ src, alt, ...props }) => {
              // Convert relative paths to asset paths with base path
              const basePath = import.meta.env.BASE_URL || '/community/'
              const imageSrc = src?.startsWith('/')
                ? src
                : `${basePath}assets/manual/${src}`

              return (
                <figure className="my-6">
                  <img
                    src={imageSrc}
                    alt={alt || ''}
                    className="w-full rounded-lg shadow-md border"
                    loading="lazy"
                    onError={(e) => {
                      console.error('Image load error:', imageSrc)
                      e.currentTarget.style.display = 'none'
                    }}
                    {...props}
                  />
                  {alt && (
                    <figcaption className="text-center text-sm text-gray-500 mt-2">
                      {alt}
                    </figcaption>
                  )}
                </figure>
              )
            },
            // Custom table styling
            table: ({ children, ...props }) => (
              <div className="overflow-x-auto my-6">
                <table className="min-w-full divide-y divide-gray-200" {...props}>
                  {children}
                </table>
              </div>
            ),
            thead: ({ children, ...props }) => (
              <thead className="bg-gray-50" {...props}>{children}</thead>
            ),
            th: ({ children, ...props }) => (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" {...props}>
                {children}
              </th>
            ),
            td: ({ children, ...props }) => (
              <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100" {...props}>
                {children}
              </td>
            ),
            // Custom blockquote for notes
            blockquote: ({ children }) => (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-4 rounded-r">
                <div className="text-blue-800">{children}</div>
              </div>
            ),
            // Code blocks
            code: ({ className, children, ...props }) => {
              const isBlock = className?.includes('language-')
              if (isBlock) {
                return (
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <code className={className} {...props}>{children}</code>
                  </pre>
                )
              }
              return (
                <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm" {...props}>
                  {children}
                </code>
              )
            },
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </article>

      {/* Screenshots Section */}
      {section.screenshots && section.screenshots.length > 0 && (
        <section className="mt-12 border-t pt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Screenshot</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {section.screenshots.map((screenshot) => {
              const basePath = import.meta.env.BASE_URL || '/community/'
              const imgPath = screenshot.path.startsWith('/')
                ? `${basePath}${screenshot.path.slice(1)}`
                : screenshot.path
              return (
                <figure key={screenshot.id} className="group">
                  <a
                    href={imgPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block overflow-hidden rounded-lg border shadow-sm hover:shadow-md transition-shadow"
                  >
                    <img
                      src={imgPath}
                      alt={screenshot.alt}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </a>
                  <figcaption className="text-sm text-gray-500 mt-2 text-center">
                    {screenshot.caption}
                  </figcaption>
                </figure>
              )
            })}
          </div>
        </section>
      )}

      {/* Navigation */}
      <nav className="mt-12 pt-8 border-t flex items-center justify-between">
        {navigation.prev ? (
          <Link
            to={`/help/${navigation.prev.chapterId}/${navigation.prev.sectionId}`}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600 transition-colors"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            <div>
              <p className="text-xs text-gray-400">Precedente</p>
              <p className="font-medium">{navigation.prev.title}</p>
            </div>
          </Link>
        ) : <div />}

        {navigation.next ? (
          <Link
            to={`/help/${navigation.next.chapterId}/${navigation.next.sectionId}`}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600 transition-colors text-right"
          >
            <div>
              <p className="text-xs text-gray-400">Successivo</p>
              <p className="font-medium">{navigation.next.title}</p>
            </div>
            <ChevronRightIcon className="h-4 w-4" />
          </Link>
        ) : <div />}
      </nav>
    </div>
  )
}
